/* /tools/dinner-plate/events/:id — host dashboard (share link, kitchen breakdown, guest orders) */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Copy, Check, Users, ChefHat, Loader2, Trash2, FileDown, FileSpreadsheet, ChevronDown, QrCode, Download, Clock, ChevronUp } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useAuth } from "../contexts/AuthContext";
import { dp } from "../lib/dinnerPlateApi";
import { toast } from "sonner";

const CCY_SYM = { GBP: "£", EUR: "€", USD: "$" };
const money = (n, sym) => (n == null ? "—" : `${sym}${Number(n).toFixed(2)}`);

export default function DinnerPlateEventPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(null);   // 'pdf' | 'xlsx' | null
  const [openIds, setOpenIds] = useState(() => new Set());  // guest order_numbers currently expanded
  const [showQr, setShowQr] = useState(false);
  const [showChef, setShowChef] = useState(false);          // mobile toggle

  async function load() {
    try { setData(await dp.summary(id)); } catch { toast.error("Couldn't load event"); }
  }
  useEffect(() => { if (user) load(); }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const {
    event, guest_orders = [], chef_aggregate = [], guest_count,
    grand_total, has_prices,
    currency_symbol: sym = CCY_SYM[event?.currency] || "£",
    order_deadline, orders_closed,
  } = data;
  const shareUrl = `${window.location.origin}/dinner-plate/e/${event.slug}`;
  const deadlineDate = order_deadline ? new Date(order_deadline) : null;

  function toggle(orderNum) {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(orderNum)) next.delete(orderNum); else next.add(orderNum);
      return next;
    });
  }
  const allOpen = openIds.size > 0 && openIds.size === guest_orders.length;
  function toggleAll() {
    setOpenIds(allOpen ? new Set() : new Set(guest_orders.map(g => g.order_number)));
  }

  async function copyLink() {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function download(kind) {
    setDownloading(kind);
    try { await dp.download(id, kind); toast.success(`${kind.toUpperCase()} downloaded`); }
    catch { toast.error(`Couldn't export ${kind.toUpperCase()}`); }
    finally { setDownloading(null); }
  }

  function downloadQr() {
    const canvas = document.querySelector("[data-testid='dp-qr-canvas'] canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${event.slug}-qr.png`;
    document.body.appendChild(a); a.click(); a.remove();
  }

  async function del() {
    if (!window.confirm("Delete this event and all its orders?")) return;
    try { await dp.deleteEvent(id); toast.success("Event deleted"); window.location.href = "/tools/dinner-plate"; }
    catch { toast.error("Couldn't delete"); }
  }

  return (
    <div className="animate-fade-up" data-testid="dp-event-page">
      <Link to="/tools/dinner-plate" className="inline-flex items-center gap-1 text-sm mb-4 hover:underline" style={{ color: "var(--text-muted)" }}>
        <ChevronLeft className="h-4 w-4" /> All dinners
      </Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-black tracking-tighter">{event.title}</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            {event.restaurant_name}{event.event_date && ` · ${new Date(event.event_date).toLocaleString()}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => download("pdf")} disabled={downloading !== null || guest_count === 0}
                  data-testid="dp-export-pdf"
                  title={guest_count === 0 ? "Add at least one guest order first" : "Download printable PDF"}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold border disabled:opacity-50"
                  style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}>
            {downloading === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
            PDF
          </button>
          <button onClick={() => download("xlsx")} disabled={downloading !== null || guest_count === 0}
                  data-testid="dp-export-xlsx"
                  title={guest_count === 0 ? "Add at least one guest order first" : "Download Excel workbook"}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold border disabled:opacity-50"
                  style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}>
            {downloading === "xlsx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
            Excel
          </button>
          <button onClick={del} data-testid="dp-delete" className="h-9 w-9 rounded-lg flex items-center justify-center hover:bg-[color:var(--surface-2)]"
                  style={{ color: "var(--error)" }}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Order deadline banner */}
      {deadlineDate && (
        <div className="rounded-xl p-3 mb-4 flex items-center gap-2 text-sm" data-testid="dp-deadline-banner"
             style={{
               background: orders_closed ? "rgba(220, 38, 38, 0.08)" : "var(--primary-soft)",
               color: orders_closed ? "var(--error)" : "var(--primary-ink)",
             }}>
          <Clock className="h-4 w-4 shrink-0" />
          {orders_closed
            ? <span><strong>Orders closed.</strong> Deadline was {deadlineDate.toLocaleString()}. Late guests will see a friendly closed screen.</span>
            : <span><strong>Orders close</strong> {deadlineDate.toLocaleString()}</span>}
        </div>
      )}

      {/* Share URL card w/ QR */}
      <div className="card-tactile rounded-2xl p-5 mb-5" data-testid="dp-share-card">
        <div className="label-caps mb-2">Share this link with your guests</div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="flex-1 min-w-0 text-sm px-3 h-10 rounded-lg flex items-center overflow-x-auto"
                style={{ background: "var(--surface-2)", color: "var(--text-main)" }}>{shareUrl}</code>
          <button onClick={copyLink} data-testid="dp-copy-link"
                  className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg text-sm font-semibold border"
                  style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
          </button>
          <button onClick={() => setShowQr(v => !v)} data-testid="dp-qr-toggle"
                  aria-expanded={showQr}
                  className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg text-sm font-semibold border"
                  style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}>
            <QrCode className="h-4 w-4" /> {showQr ? "Hide QR" : "QR code"}
          </button>
        </div>
        {showQr && (
          <div className="mt-4 flex flex-col sm:flex-row items-center gap-4" data-testid="dp-qr-panel">
            <div className="bg-white p-3 rounded-lg" data-testid="dp-qr-canvas">
              <QRCodeCanvas value={shareUrl} size={168} includeMargin={false} level="M" />
            </div>
            <div className="flex-1 text-sm" style={{ color: "var(--text-muted)" }}>
              <p>Guests can scan this code with their phone camera — no app needed.</p>
              <button onClick={downloadQr} data-testid="dp-qr-download"
                      className="mt-2 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold border"
                      style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}>
                <Download className="h-3.5 w-3.5" /> Download PNG
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Two-column: chef aggregate rail + per-guest breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-5">
        {/* Chef aggregate */}
        <aside className="card-tactile rounded-2xl p-5 lg:sticky lg:top-4 lg:self-start" data-testid="dp-chef-aggregate">
          <button
            onClick={() => setShowChef(v => !v)}
            aria-expanded={showChef}
            data-testid="dp-chef-toggle"
            className="lg:hidden w-full flex items-center justify-between mb-3 text-left"
          >
            <h2 className="font-display tracking-tight text-lg flex items-center gap-2">
              <ChefHat className="h-4 w-4" style={{ color: "var(--primary-ink)" }} /> Chef view
              <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>bulk totals</span>
            </h2>
            {showChef ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <h2 className="hidden lg:flex font-display tracking-tight text-lg items-center gap-2 mb-3">
            <ChefHat className="h-4 w-4" style={{ color: "var(--primary-ink)" }} /> Chef view
            <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>bulk totals</span>
          </h2>
          <div className={`${showChef ? "block" : "hidden"} lg:block`}>
            {chef_aggregate.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>No orders yet.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="dp-chef-list">
                {chef_aggregate.map((k, i) => (
                  <li key={i} data-testid={`dp-chef-row-${i}`}
                      className="flex items-center justify-between text-sm gap-3 py-1 border-b last:border-0"
                      style={{ borderColor: "var(--border-subtle)" }}>
                    <span className="min-w-0 truncate">
                      {k.title}
                      {k.option && <span className="text-xs block" style={{ color: "var(--text-muted)" }}>{k.option}</span>}
                    </span>
                    <span className="shrink-0 font-semibold" style={{ color: "var(--primary-ink)" }}>×{k.quantity}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Kitchen breakdown — per guest */}
        <section className="card-tactile rounded-2xl p-5 min-w-0" data-testid="dp-kitchen-by-guest">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h2 className="font-display tracking-tight text-lg flex items-center gap-2">
              <Users className="h-4 w-4" style={{ color: "var(--primary-ink)" }} />
              Guests
              <span className="text-sm font-normal" style={{ color: "var(--text-muted)" }}>({guest_count})</span>
            </h2>
            <div className="flex items-center gap-3">
              {has_prices && guest_orders.length > 0 && (
                <div className="text-sm" data-testid="dp-grand-total">
                  <span style={{ color: "var(--text-muted)" }}>Grand total </span>
                  <span className="font-display font-bold text-base" style={{ color: "var(--primary-ink)" }}>{money(grand_total, sym)}</span>
                </div>
              )}
              {guest_orders.length > 1 && (
                <button onClick={toggleAll} data-testid="dp-toggle-all"
                        className="text-xs font-semibold hover:underline"
                        style={{ color: "var(--primary-ink)" }}>
                  {allOpen ? "Collapse all" : "Expand all"}
                </button>
              )}
            </div>
          </div>

          {guest_orders.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Waiting for orders. Share the link above ↑</p>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
              {guest_orders.map(g => {
                const open = openIds.has(g.order_number);
                return (
                  <li key={g.order_number} data-testid={`dp-order-${g.order_number}`} className="py-2">
                    <button
                      onClick={() => toggle(g.order_number)}
                      aria-expanded={open}
                      data-testid={`dp-guest-toggle-${g.order_number}`}
                      className="w-full flex items-center gap-3 text-left rounded-lg px-2 py-2 hover:bg-[color:var(--surface-2)] transition-colors"
                    >
                      <ChevronDown
                        className="h-4 w-4 shrink-0 transition-transform"
                        style={{
                          color: "var(--text-muted)",
                          transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-semibold text-sm truncate">{g.guest_name}</span>
                          <span className="text-xs font-mono shrink-0" style={{ color: "var(--text-muted)" }}>{g.order_number}</span>
                        </div>
                        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                          {g.items.length} item{g.items.length === 1 ? "" : "s"}
                          {g.special_dietary_notes && <span> · {g.special_dietary_notes}</span>}
                        </div>
                      </div>
                      {has_prices && (
                        <span className="shrink-0 text-sm font-semibold" data-testid={`dp-guest-total-${g.order_number}`}
                              style={{ color: "var(--primary-ink)" }}>
                          {money(g.total, sym)}
                        </span>
                      )}
                    </button>

                    {open && (
                      <div className="mt-1 sm:ml-7 mr-0 sm:mr-2 rounded-lg overflow-x-auto"
                           data-testid={`dp-guest-details-${g.order_number}`}
                           style={{ background: "var(--surface-2)" }}>
                        <table className="w-full text-xs min-w-[380px]">
                          <thead>
                            <tr style={{ color: "var(--text-muted)" }}>
                              <th className="text-left font-medium px-3 py-1.5">Item</th>
                              <th className="text-left font-medium px-2 py-1.5">Option</th>
                              <th className="text-right font-medium px-2 py-1.5 w-10">Qty</th>
                              {has_prices && <>
                                <th className="text-right font-medium px-2 py-1.5 w-16">Unit</th>
                                <th className="text-right font-medium px-3 py-1.5 w-20">Line</th>
                              </>}
                            </tr>
                          </thead>
                          <tbody>
                            {g.items.map((it, i) => (
                              <tr key={i} className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
                                <td className="px-3 py-1.5 font-medium">{it.title}</td>
                                <td className="px-2 py-1.5" style={{ color: "var(--text-muted)" }}>{it.selected_option || "—"}</td>
                                <td className="px-2 py-1.5 text-right whitespace-nowrap">{it.quantity}</td>
                                {has_prices && <>
                                  <td className="px-2 py-1.5 text-right whitespace-nowrap" style={{ color: "var(--text-muted)" }}>{money(it.unit_price, sym)}</td>
                                  <td className="px-3 py-1.5 text-right font-medium whitespace-nowrap">{money(it.line_total, sym)}</td>
                                </>}
                              </tr>
                            ))}
                            {has_prices && (
                              <tr className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
                                <td colSpan={4} className="px-3 py-1.5 text-right font-semibold">Order total</td>
                                <td className="px-3 py-1.5 text-right font-bold whitespace-nowrap" style={{ color: "var(--primary-ink)" }}>{money(g.total, sym)}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
